import { useMemo } from "react";
import { ReceiptText } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { ToolHeader } from "@/components/common/ToolHeader";
import { useCalculation } from "@/components/common/useCalculation";
import { TAX_DEFAULT, runPph21Tetap, calcPph21TidakTetap, calcPph21BukanPegawai, calcPph23, calcPph42 } from "@/lib/engines/tax";
import { fmtRp, fmtPct } from "@/lib/format";
import Pph21Panel from "@/components/tax/Pph21Panel";
import Pph23Panel from "@/components/tax/Pph23Panel";
import Pph42Panel from "@/components/tax/Pph42Panel";

export default function TaxToolPage() {
  const calc = useCalculation("pajak", TAX_DEFAULT, "Perhitungan Pajak");
  const { data, setData, readOnly, loading } = calc;

  const res = useMemo(() => {
    const p21 = data.pph21;
    let r21;
    if (p21.employeeType === "tidakTetap") r21 = calcPph21TidakTetap(p21.tidakTetap);
    else if (p21.employeeType === "bukanPegawai") r21 = calcPph21BukanPegawai(p21.bukanPegawai);
    else r21 = runPph21Tetap(p21.tetap);
    return { pph21: r21, pph23: calcPph23(data.pph23), pph42: calcPph42(data.pph42) };
  }, [data]);

  const update = (key) => (updater) =>
    setData((d) => ({ ...d, [key]: typeof updater === "function" ? updater(d[key]) : updater }));

  const getSummary = () => {
    if (data.tab === "pph23") {
      const r = res.pph23;
      return { headline: `PPh 23 ${fmtRp(r.pph)}`, tab: "pph23", values: { dpp: r.dpp, pph: r.pph, ppn: r.ppn } };
    }
    if (data.tab === "pph42") {
      const r = res.pph42;
      return { headline: `PPh 4(2) ${fmtRp(r.pph)}`, tab: "pph42", values: { bruto: r.bruto, pph: r.pph } };
    }
    const t = data.pph21.employeeType;
    const r = res.pph21;
    if (t === "tetap") return { headline: `PPh 21 ${fmtRp(r.regular.pph)}/bln · setahun ${fmtRp(r.annual.tax)}`, tab: "pph21", values: { annual: r.annual.tax, thp: r.regular.thp } };
    return { headline: `PPh 21 ${fmtRp(r.pph)}`, tab: "pph21", values: { pph: r.pph } };
  };

  const getAiContext = () => {
    if (data.tab === "pph23") return { jenis: "PPh 23", input: data.pph23, hasil: res.pph23 };
    if (data.tab === "pph42") return { jenis: "PPh 4 ayat 2", input: data.pph42, hasil: res.pph42 };
    const t = data.pph21.employeeType;
    if (t === "tetap") {
      const r = res.pph21;
      return {
        jenis: "PPh 21 Pegawai Tetap",
        input: data.pph21.tetap,
        hasil: {
          kategoriTER: r.category,
          ptkp: r.ptkp,
          gajiPokokHasilSolver: r.solvedGajiPokok,
          bulanReguler: { bruto: r.regular.bruto, tarifTER: r.regular.terRate, pph: r.regular.pph, takeHome: r.regular.thp, biayaPerusahaan: r.regular.companyCost },
          desember: { pph: r.pphDec },
          tahunan: { bruto: r.annual.totalBruto, biayaJabatan: r.annual.biayaJabatan, iuran: r.annual.iuran, neto: r.annual.neto, pkp: r.annual.pkp, pphSetahun: r.annual.tax },
          takeHomeSetahun: r.thpAnnual,
          biayaPerusahaanSetahun: r.costAnnual,
          tarifEfektif: fmtPct(r.effectiveRate),
        },
      };
    }
    return { jenis: t === "tidakTetap" ? "PPh 21 Pegawai Tidak Tetap" : "PPh 21 Bukan Pegawai", input: data.pph21[t], hasil: res.pph21 };
  };

  return (
    <div className="max-w-[1400px] mx-auto">
      <ToolHeader
        icon={ReceiptText}
        eyebrow="Tool 01 · Pajak"
        title="Kalkulator Pajak"
        description="Hitung PPh 21 (skema TER PP 58/2023 + rekonsiliasi Desember Pasal 17), PPh 23, dan PPh Final 4 ayat (2) dengan pilihan metode gross, gross-up, atau net."
        calc={calc}
        tool="pajak"
        getSummary={getSummary}
        getAiContext={getAiContext}
      />
      {loading ? (
        <Skeleton className="h-[480px] bg-[hsl(220_10%_10%)]" />
      ) : (
        <Tabs value={data.tab} onValueChange={(v) => setData((d) => ({ ...d, tab: v }))}>
          <TabsList className="bg-[hsl(220_12%_8%)] border border-border h-auto p-1 flex-wrap" data-testid="tax-tabs">
            <TabsTrigger value="pph21" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="tax-tab-pph21">
              PPh 21 · Karyawan
            </TabsTrigger>
            <TabsTrigger value="pph23" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="tax-tab-pph23">
              PPh 23 · Jasa & Sewa
            </TabsTrigger>
            <TabsTrigger value="pph42" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="tax-tab-pph42">
              PPh 4(2) · Final
            </TabsTrigger>
          </TabsList>
          <TabsContent value="pph21" className="mt-5">
            <Pph21Panel data={data.pph21} setData={update("pph21")} result={res.pph21} readOnly={readOnly} />
          </TabsContent>
          <TabsContent value="pph23" className="mt-5">
            <Pph23Panel data={data.pph23} setData={update("pph23")} result={res.pph23} readOnly={readOnly} />
          </TabsContent>
          <TabsContent value="pph42" className="mt-5">
            <Pph42Panel data={data.pph42} setData={update("pph42")} result={res.pph42} readOnly={readOnly} />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
